import {Container, Graphics} from "pixi.js";
import {Polygon} from "detect-collisions";
import {lerp} from "../utils.js";
import {Vector} from "../i.js";
import {Random} from "../random.js";
import {sq} from "../i.js"; // ./dump.js
import {ConfigStructure, OptionalProperty, RequiredProperty} from "../i.js"; // ../validators.js
import {AI, AIUtils} from "../i.js";
import {FACTIONS, Thing, THING_TYPES, Z_INDICES} from "../i.js";

const SPEED = 1;  // flares / second
const STANDARD_WAVELENGTH_SQ = sq(0.3) + sq(0.15);

// Visuals

// Color of the outline of the wave
const COLOR_OUTLINE = {h: 10, s: 100, v: 100};
// Color of the center of the wave
const COLOR_CENTER = {h: 10, s: 5, v: 100};
// How close together to draw zigzags, in zigzags per flare of wavelength
const ZIGZAG_DENSITY = 20;
// If <1, zigzags move forward
// If 1, zigzags stay in place
// If >1, zigzags move backward
const ZIGZAG_MOVEMENT = 1.2;
// How jagged the zigzags are
const ZIGZAG_NOISE_DENSITY = 10;



class WaveAnimation {
	constructor(thing) {
		this.thing = thing;

		this._num_zigzags = Math.max(
			Math.round(ZIGZAG_DENSITY * this.thing.wavelength),
			2 // Make sure there is no division by 0
		);

		this._trail = new Container();
		for (let i = 0; i < this._num_zigzags; ++i) {
			this._trail.addChild(new Graphics());
		}

		this._body = new Graphics();
		this._body
			.moveTo(- this.thing.wavelength / 2, 0)
			.lineTo(this.thing.wavelength / 2, 0)
			.stroke({
				color: COLOR_OUTLINE,
				width: 0.045,
				cap: "round"
			})
			.moveTo(- this.thing.wavelength / 2, 0)
			.lineTo(this.thing.wavelength / 2, 0)
			.stroke({
				color: COLOR_CENTER,
				width: 0.02,
				cap: "round"
			});
		this.thing.display.addChild(this._trail, this._body);
	}

	update(dt, time) {
		let wavelength = this.thing.wavelength;

		for (let i = 0; i < this._num_zigzags; ++i) {
			let zigzag = this._trail.children[i];

			zigzag.clear();

			let points = [];
			let xMin = lerp(- wavelength / 2, wavelength / 2, i / this._num_zigzags);
			let xMax = xMin + wavelength / this._num_zigzags;
			let xFraction = i / (this._num_zigzags - 1);
			let yMax = Math.min(
				wavelength * (1 - 4 * Math.abs(xFraction - 0.5) ** 3),
				this.thing.distanceMoved
			);
			for (let y = 0; y < yMax; y += 0.04) {
				points.push(new Vector(
					lerp(
						xMin, xMax,
						(Random.noise(
							100 * xMin, // Noise slice to use
							ZIGZAG_NOISE_DENSITY
								* (ZIGZAG_MOVEMENT * this.thing.distanceMoved - y)
						) + 1) / 2
					),
					y
				));
			}
			zigzag
				.poly(points, false)
				.stroke({
					color: COLOR_OUTLINE,
					width: 0.015,
					cap: "round"
				});
		}
	}
}



class WaveAI extends AI {
	move(dt, time) {
		AIUtils.translate(this.thing, new Vector(), 0, dt);
		this.thing.distanceMoved += this.thing.vel.mag() * dt;
	}
}


export class Wave extends Thing {
	static CONFIG_STRUCTURE = new ConfigStructure([
		// pos of this wave, a vector with values in flares
		new RequiredProperty("pos", v => (v instanceof Vector)),
		// rot of this wave, in radians
		new RequiredProperty("rot", v => Number.isFinite(v)),
		// Square of the length of the wave
		// Defaults to correct value for compass if omitted.
		new OptionalProperty("wavelengthSq", v => (Number.isFinite(v) && v > 0))
		// Not explicitly listing health any more
	]);

	// Create a wave
	// config is an object with the properties provided by the spawn request
	// enrichment, plus the properties specified in CONFIG_STRUCTURE above.
	constructor(config) {
		Wave.CONFIG_STRUCTURE.test(config);

		let wavelengthSq = config.wavelengthSq ?? STANDARD_WAVELENGTH_SQ;

		config.zIndex = Z_INDICES.NPC_CHILD;
		config.len = Math.sqrt(wavelengthSq);
		config.hitbox = new Polygon({x: 0, y: 0}, [
			{x: 0, y: - Math.sqrt(wavelengthSq) / 2},
			{x: 0, y: Math.sqrt(wavelengthSq) / 2}
		]);
		config.type = THING_TYPES.ENERGY;
		config.faction = FACTIONS.DAMSEL;
		config.canDespawn = true;
		config.despawnSafetyWindow = 0;
		config.vel = new Vector(SPEED, 0)
			.rotateBy(config.rot);
		config.maxHealth = 6;
		super(config);

		// Store the squared wavelength to avoid rounding errors when saving
		this.wavelengthSq = wavelengthSq;
		this.wavelength = Math.sqrt(wavelengthSq);

		// Set by AI, read by animation; doesn't need to be saved
		this.distanceMoved = 0;

		this.animation = new WaveAnimation(this);
		this.ai = new WaveAI(this);
	}

	// Take damage from an attack
	// damage is a number.
	takeDamage(damage) {
		// Nothing to do here - a player can't damage a wave directly.
		// TODO: Remove on collision with a station.
	}
}
